import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, X, FileText, Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";

export function DocumentViewerModal({ open, onClose, blueprint }) {
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !blueprint) {
      setPreviewUrl(null);
      setLoading(true);
      setError(null);
      return;
    }

    const loadPreview = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${API}/blueprints/${blueprint.id}/preview`, {
          responseType: "blob",
        });
        const url = URL.createObjectURL(response.data);
        setPreviewUrl(url);
      } catch {
        setError("Unable to load file preview");
      } finally {
        setLoading(false);
      }
    };

    loadPreview();
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [open, blueprint?.id]);

  if (!blueprint) return null;

  const isPdf = blueprint.content_type === "application/pdf";
  const isImage = blueprint.content_type?.startsWith("image/");

  const handleDownload = () => {
    if (previewUrl) {
      const a = document.createElement("a");
      a.href = previewUrl;
      a.download = blueprint.original_filename;
      a.click();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-4xl max-h-[90vh] flex flex-col" aria-describedby="doc-viewer-desc">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="font-heading font-bold text-[#0F172A] truncate pr-4">
              {blueprint.original_filename}
            </DialogTitle>
            <Button
              size="sm"
              variant="outline"
              onClick={handleDownload}
              disabled={!previewUrl}
              className="rounded-none border-[#E2E8F0] shrink-0"
              data-testid="download-preview-button"
            >
              <Download size={14} className="mr-1" /> Download
            </Button>
          </div>
          <div id="doc-viewer-desc" className="text-xs text-[#64748B] font-mono mt-1">
            {blueprint.content_type} &middot; v{blueprint.version || 1}
            {blueprint.machine_number && <> &middot; Machine: {blueprint.machine_number}</>}
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-auto bg-[#F1F5F9] border border-[#E2E8F0]" data-testid="document-preview-area">
          {loading && (
            <div className="flex items-center justify-center h-96">
              <Loader2 size={24} className="animate-spin text-[#2563EB]" />
              <span className="ml-2 text-[#64748B]">Loading preview...</span>
            </div>
          )}
          {error && (
            <div className="flex flex-col items-center justify-center h-96 text-[#64748B]">
              <FileText size={48} className="mb-3 text-[#CBD5E1]" />
              <p>{error}</p>
            </div>
          )}
          {!loading && !error && isPdf && previewUrl && (
            <iframe
              src={previewUrl}
              className="w-full h-[70vh]"
              title="Blueprint Preview"
              data-testid="pdf-preview-iframe"
            />
          )}
          {!loading && !error && isImage && previewUrl && (
            <div className="flex items-center justify-center p-4">
              <img
                src={previewUrl}
                alt={blueprint.original_filename}
                className="max-w-full max-h-[70vh] object-contain"
                data-testid="image-preview"
              />
            </div>
          )}
          {!loading && !error && !isPdf && !isImage && (
            <div className="flex flex-col items-center justify-center h-96 text-[#64748B]">
              <FileText size={48} className="mb-3 text-[#CBD5E1]" />
              <p>Preview not available for this file type</p>
              <p className="text-xs mt-1">{blueprint.content_type}</p>
              <Button
                size="sm"
                onClick={handleDownload}
                className="mt-4 bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none"
              >
                <Download size={14} className="mr-1" /> Download File
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
